import React from "react";
import SEOLandingPage from "./SEOLandingPage";

const SshClientAndroid: React.FC = () => (
  <SEOLandingPage
    title="SSH Client for Android — Terminal Emulator & Server Management | Cosmovex"
    metaDescription="ShellPilot is a full SSH client for Android. Connect to servers, run terminal commands, manage Docker containers, and monitor logs — all from your phone."
    ogTitle="SSH Client for Android — ShellPilot Terminal"
    h1="SSH Client for Android"
    intro="A proper SSH client on Android. ShellPilot connects to remote servers with full terminal emulation — run commands, manage services, and monitor logs from your phone."
    paragraphs={[
      "When a server needs attention at 2am and you're not near a laptop, ShellPilot is what you reach for. It's a full-featured SSH client for Android that gives you a real terminal session with your remote servers — not a limited web UI or a toy app, but a genuine SSH connection with PTY support.",
      "The terminal emulator handles the things that matter: ANSI color codes, so tools like htop, vim, and ncdu display correctly; proper key sequences so you can use Ctrl+C, Ctrl+Z, and arrow keys; and a scrollback buffer so you don't lose output that scrolls past the screen.",
      "Managing multiple servers is straightforward. Add each host once with its IP, port, username, and authentication method (password or private key), and it's available in one tap. You can keep connections to staging, production, and personal servers organized and switch between them quickly.",
      "Beyond basic SSH, ShellPilot understands developer workflows. Connect to a server, run docker ps, tail your application logs, check nginx status, run a database query — the app gets out of your way and lets you work. The keyboard includes keys developers actually use: Tab, Escape, pipe, slash, and function keys.",
    ]}
    features={[
      "Full SSH2 protocol with PTY terminal emulation",
      "ANSI color support — htop, vim, tmux all work",
      "Password and SSH private key authentication",
      "Multiple server profiles with one-tap connect",
      "Full keyboard with Tab, Escape, and function keys",
      "Scrollback buffer for reviewing past output",
      "Port forwarding support",
      "Connection persistence across screen rotation",
      "Fingerprint authentication to unlock app",
      "Dark terminal theme with adjustable font size",
    ]}
    howToTitle="How to connect to a server over SSH on Android"
    howToSteps={[
      { name: "Install ShellPilot", text: "Download ShellPilot free from Google Play — a full SSH client for Android." },
      { name: "Add a connection", text: "Tap New Connection and enter the server address, port (usually 22), and username." },
      { name: "Authenticate", text: "Choose a password or import an SSH private key for key-based login." },
      { name: "Open a terminal", text: "Tap the saved host to start a terminal session and run commands on your server." },
    ]}
    useCases={[
      { title: "Restart a service away from your desk", body: "Open the saved host, run systemctl restart, and confirm recovery — no laptop needed." },
      { title: "Tail logs after a deploy", body: "Connect, tail the application log, and verify the release looks clean from your phone." },
      { title: "Quick edits in vim", body: "Open a config in vim over SSH, make the fix, save, and reload the service." },
    ]}
    faqs={[
      { q: "Is ShellPilot a real SSH client?", a: "Yes. It uses the SSH2 protocol with a PTY-backed terminal, so you get a genuine remote shell — not a limited web UI or local sandbox." },
      { q: "Does it support SSH key authentication?", a: "Yes. You can authenticate with a password or import an SSH private key for key-based login." },
      { q: "Can I manage several servers?", a: "Yes. Save a profile per server with its own host, port, and credentials, and connect to any of them in one tap." },
      { q: "Is it free?", a: "ShellPilot is free to download and use, with ads and an optional one-time Pro upgrade." },
    ]}
    related={[
      { label: "PuTTY for Android", path: "/putty-for-android", tagline: "The mobile PuTTY alternative" },
      { label: "Terminal Emulator for Android", path: "/terminal-emulator-android", tagline: "ANSI colors and real key sequences" },
      { label: "SFTP Client for Android", path: "/sftp-client-android", tagline: "Transfer files to remote servers" },
      { label: "Manage Servers from Your Phone", path: "/manage-server-from-phone", tagline: "Run, monitor, and fix servers anywhere" },
      { label: "Termux Alternative for Android", path: "/termux-alternative-android", tagline: "SSH with a real UI, no sideloading" },
    ]}
    playStoreUrl="https://play.google.com/store/apps/details?id=com.cosmovex.shellpilot"
    icon="/images/shellpilot.png"
    appName="ShellPilot"
    accentColor="#A8720E"
    canonicalPath="/ssh-client-android"
  />
);

export default SshClientAndroid;
